import { createTheme, Theme, PaletteMode } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { RootState } from "../store";

// Define theme colors for different modes
const themeColors = {
  light: {
    primary: {
      main: "#2563eb",
      light: "#60a5fa",
      dark: "#1d4ed8",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#64748b",
      light: "#94a3b8",
      dark: "#475569",
      contrastText: "#ffffff",
    },
    error: {
      main: "#ef4444",
      light: "#f87171",
      dark: "#dc2626",
      contrastText: "#ffffff",
    },
    warning: {
      main: "#f59e0b",
      light: "#fbbf24",
      dark: "#d97706",
      contrastText: "#ffffff",
    },
    success: {
      main: "#22c55e",
      light: "#4ade80",
      dark: "#16a34a",
      contrastText: "#ffffff",
    },
    background: {
      default: "#f8fafc",
      paper: "#ffffff",
    },
    text: {
      primary: "#111827",
      secondary: "#6b7280",
    },
    divider: "#e5e7eb",
    info: {
      main: "#3b82f6",
      light: "#60a5fa",
      dark: "#1d4ed8",
      contrastText: "#ffffff",
    },
    action: {
      active: "#6b7280",
      hover: "rgba(107, 114, 128, 0.04)",
      selected: "rgba(107, 114, 128, 0.08)",
      disabled: "rgba(107, 114, 128, 0.26)",
      disabledBackground: "rgba(107, 114, 128, 0.12)",
    },
    default: {
      main: "#6b7280",
      light: "#9ca3af",
      dark: "#4b5563",
      contrastText: "#ffffff",
    },
  },
  dark: {
    primary: {
      main: "#3b82f6",
      light: "#60a5fa",
      dark: "#1d4ed8",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#8b5cf6",
      light: "#a78bfa",
      dark: "#7c3aed",
      contrastText: "#ffffff",
    },
    error: {
      main: "#f87171",
      light: "#fca5a5",
      dark: "#ef4444",
      contrastText: "#ffffff",
    },
    warning: {
      main: "#fbbf24",
      light: "#fcd34d",
      dark: "#f59e0b",
      contrastText: "#ffffff",
    },
    success: {
      main: "#4ade80",
      light: "#86efac",
      dark: "#22c55e",
      contrastText: "#ffffff",
    },
    background: {
      default: "#0f172a",
      paper: "#1e293b",
    },
    text: {
      primary: "#f8fafc",
      secondary: "#cbd5e1",
    },
    divider: "#334155",
    info: {
      main: "#3b82f6",
      light: "#60a5fa",
      dark: "#1d4ed8",
      contrastText: "#ffffff",
    },
    action: {
      active: "#cbd5e1",
      hover: "rgba(203, 213, 225, 0.04)",
      selected: "rgba(203, 213, 225, 0.08)",
      disabled: "rgba(203, 213, 225, 0.26)",
      disabledBackground: "rgba(203, 213, 225, 0.12)",
    },
    default: {
      main: "#cbd5e1",
      light: "#e2e8f0",
      dark: "#94a3b8",
      contrastText: "#0f172a",
    },
  },
  bw: {
    primary: {
      main: "#000000",
      light: "#404040",
      dark: "#000000",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#666666",
      light: "#a3a3a3",
      dark: "#404040",
      contrastText: "#ffffff",
    },
    error: {
      main: "#000000",
      light: "#404040",
      dark: "#000000",
      contrastText: "#ffffff",
    },
    warning: {
      main: "#000000",
      light: "#404040",
      dark: "#000000",
      contrastText: "#ffffff",
    },
    success: {
      main: "#000000",
      light: "#404040",
      dark: "#000000",
      contrastText: "#ffffff",
    },
    background: {
      default: "#ffffff",
      paper: "#f5f5f5",
    },
    text: {
      primary: "#000000",
      secondary: "#666666",
    },
    divider: "#cccccc",
    info: {
      main: "#000000",
      light: "#404040",
      dark: "#000000",
      contrastText: "#ffffff",
    },
    action: {
      active: "#666666",
      hover: "rgba(102, 102, 102, 0.04)",
      selected: "rgba(102, 102, 102, 0.08)",
      disabled: "rgba(102, 102, 102, 0.26)",
      disabledBackground: "rgba(102, 102, 102, 0.12)",
    },
    default: {
      main: "#666666",
      light: "#a3a3a3",
      dark: "#404040",
      contrastText: "#ffffff",
    },
  },
};

// Create theme based on mode
export const createAppTheme = (mode: "light" | "dark" | "bw"): Theme => {
  const colors = themeColors[mode];

  return createTheme({
    palette: {
      mode: mode === "bw" ? "light" : (mode as PaletteMode),
      ...colors,
    },
    typography: {
      fontFamily: [
        "Inter",
        "-apple-system",
        "BlinkMacSystemFont",
        '"Segoe UI"',
        "Roboto",
        '"Helvetica Neue"',
        "Arial",
        "sans-serif",
      ].join(","),
      h1: {
        fontWeight: 700,
        fontSize: "2.5rem",
        lineHeight: 1.2,
        letterSpacing: "-0.025em",
      },
      h2: {
        fontWeight: 600,
        fontSize: "2rem",
        lineHeight: 1.3,
        letterSpacing: "-0.025em",
      },
      h3: {
        fontWeight: 600,
        fontSize: "1.75rem",
        lineHeight: 1.3,
        letterSpacing: "-0.025em",
      },
      h4: {
        fontWeight: 600,
        fontSize: "1.5rem",
        lineHeight: 1.4,
        letterSpacing: "0em",
      },
      h5: {
        fontWeight: 600,
        fontSize: "1.25rem",
        lineHeight: 1.4,
        letterSpacing: "0em",
      },
      h6: {
        fontWeight: 600,
        fontSize: "1.125rem",
        lineHeight: 1.4,
        letterSpacing: "0em",
      },
      subtitle1: {
        fontWeight: 600,
        fontSize: "1rem",
        lineHeight: 1.5,
        letterSpacing: "0em",
      },
      subtitle2: {
        fontWeight: 500,
        fontSize: "0.875rem",
        lineHeight: 1.5,
        letterSpacing: "0em",
      },
      body1: {
        fontSize: "1rem",
        lineHeight: 1.6,
        fontWeight: 400,
        letterSpacing: "0em",
      },
      body2: {
        fontSize: "0.875rem",
        lineHeight: 1.6,
        fontWeight: 400,
        letterSpacing: "0em",
      },
      caption: {
        fontSize: "0.75rem",
        lineHeight: 1.4,
        fontWeight: 400,
        letterSpacing: "0.025em",
      },
      button: {
        fontWeight: 500,
        textTransform: "none",
        letterSpacing: "0.025em",
      },
      overline: {
        fontSize: "0.75rem",
        fontWeight: 500,
        lineHeight: 1.4,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
      },
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            borderRadius: 8,
            fontWeight: 500,
            padding: "8px 16px",
            minHeight: 40,
          },
          contained: {
            boxShadow: "none",
            "&:hover": {
              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
            },
          },
          outlined: {
            borderWidth: 1.5,
            "&:hover": {
              borderWidth: 1.5,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            boxShadow:
              mode === "dark"
                ? "0 1px 3px 0 rgba(0, 0, 0, 0.3), 0 1px 2px -1px rgba(0, 0, 0, 0.3)"
                : "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)",
          },
          elevation1: {
            boxShadow:
              mode === "dark"
                ? "0 1px 3px 0 rgba(0, 0, 0, 0.3), 0 1px 2px -1px rgba(0, 0, 0, 0.3)"
                : "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)",
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow:
              mode === "dark"
                ? "0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)"
                : "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${colors.divider}`,
            padding: "12px 16px",
          },
          head: {
            fontWeight: 600,
            backgroundColor: mode === "dark" ? "#1e293b" : "#f8fafc",
            borderBottom: `1px solid ${colors.divider}`,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            "&:last-child td": {
              borderBottom: 0,
            },
            "&:hover": {
              backgroundColor:
                mode === "dark"
                  ? "rgba(255, 255, 255, 0.04)"
                  : "rgba(0, 0, 0, 0.04)",
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            height: 24,
            borderRadius: 6,
            fontWeight: 500,
          },
          label: {
            fontSize: "0.75rem",
            padding: "0 8px",
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            backgroundColor: mode === "dark" ? "#334155" : "#e2e8f0",
          },
          bar: {
            borderRadius: 4,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              borderRadius: 8,
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 12,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow:
              mode === "dark"
                ? "0 1px 3px 0 rgba(0, 0, 0, 0.3)"
                : "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: `1px solid ${colors.divider}`,
          },
        },
      },
    },
  });
};

// Hook to get current theme
export const useAppTheme = () => {
  const { currentTheme } = useSelector((state: RootState) => state.theme);
  return createAppTheme(currentTheme);
};

// Export theme colors for reference
export { themeColors };
